import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const AlexMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-2 list-disc space-y-1 pl-4">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 list-decimal space-y-1 pl-4">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => (
    <p className="mb-2 text-base font-semibold text-gray-900">{children}</p>
  ),
  h2: ({ children }) => (
    <p className="mb-2 text-sm font-semibold text-gray-900">{children}</p>
  ),
  h3: ({ children }) => (
    <p className="mb-1.5 text-sm font-semibold text-gray-900">{children}</p>
  ),
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded bg-gray-200/80 px-2 py-1.5 font-mono text-xs">
      {children}
    </pre>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = /\blanguage-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-gray-200/90 px-1 py-0.5 font-mono text-[0.85em]"
        {...props}
      >
        {children}
      </code>
    );
  },
  a: ({ href, children }) => (
    <a
      href={href}
      className="font-medium text-[#1a5172] underline underline-offset-2 hover:text-[#164a65]"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-gray-300 pl-3 text-gray-700">
      {children}
    </blockquote>
  ),
};

type AlexMarkdownMessageProps = {
  content: string;
  className?: string;
};

export function AlexMarkdownMessage({ content, className }: AlexMarkdownMessageProps) {
  return (
    <div className={className ?? ''}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={AlexMarkdownComponents}
      >
        {content}
      </Markdown>
    </div>
  );
}
